import { FeatureApiResponse, GrowthBook } from '@growthbook/growthbook';
import type { ExperimentOverride } from '@gumtree/shared/src/types/web-bff';
import { trackGA4Event } from '@gumtree/shared/src/util/ga4-shared';
import { useEffect, useState } from 'react';

import logger from '../logger';

export function initGrowthbook(
    growthbookExperimentData: FeatureApiResponse,
    appViewDevice?: 'iOS' | 'Android' | null,
    gbUserPseudoId?: string,
    includeTrackingCallback?: boolean
) {
    const growthbook = new GrowthBook({
        ...(includeTrackingCallback && {
            trackingCallback: (experiment, result) => {
                trackGA4Event({
                    event: 'experiment_viewed',
                    experimentId: experiment.key,
                    variationId: result.key,
                });
            },
        }),
    });

    growthbook.initSync({
        payload: growthbookExperimentData,
    });

    growthbook.setAttributes({
        isAppView: !!appViewDevice,
        ...(gbUserPseudoId && { gbUserPseudoId }),
    });

    return growthbook;
}

export const initForcedFeatures = (
    growthbook: GrowthBook,
    experimentOverrides: Array<ExperimentOverride>
) => {
    if (experimentOverrides && experimentOverrides.length > 0) {
        const overriddenFeatures = new Map();

        experimentOverrides.forEach((extractedExperiment) => {
            if (extractedExperiment) {
                overriddenFeatures.set(extractedExperiment.key, extractedExperiment.value);
            }
        });

        growthbook.setForcedFeatures(overriddenFeatures);
    }
};

export const getGrowthbookServerSide = (request: Express.Request) => {
    if (!request.growthbook) {
        logger.debug('Growthbook was requested on the serverside, but did not exist');
    }

    return request.growthbook;
};

export const getGrowthbookClientSide = () => {
    if (!window.growthbook) {
        logger.debug('Growthbook was requested on the clientside, but did not exist');
    }

    return window.growthbook;
};

export const useClientGrowthbookFeatureValue = (
    feature: string,
    defaultValue: string | boolean
) => {
    const [value, setValue] = useState<string | boolean>(defaultValue);
    useEffect(() => {
        const growthbook = getGrowthbookClientSide();
        if (!growthbook) return;
        setValue(growthbook.getFeatureValue(feature, defaultValue));
    }, [feature, defaultValue]);

    return value;
};
